<template>
  <div class="sa-dashboard-container">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#1e293b"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white; font-weight: 600;">
            {{ msg }}
          </span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showsnackbar = false">
            <v-icon style="color: #ffffff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- 1. EXECUTIVE HEADER -->
    <header class="sa-hero-header mb-4">
      <div class="sa-hero-text">
        <PageBreadcrumb :items="[{ text: 'Jewellery Admin' }]" class="mb-1" />
        <div class="d-flex align-center flex-wrap gap-2">
          <span class="role-badge-pill">Master Console</span>
        </div>
        <p class="sa-subtitle">
          Register new jewellery store administrators, set up store credentials, and provision access
        </p>
      </div>

      <div class="sa-hero-actions">
        <button class="action-btn outline-btn mr-2" @click="$router.push('/ViewAdmin')">
          <v-icon left size="16" color="#334155">mdi-account-multiple-outline</v-icon>
          Jewellery Stores
        </button>
        <button class="action-btn outline-btn mr-2" @click="$router.push('/DemoRequests')">
          <v-icon left size="16" color="#334155">mdi-calendar-clock-outline</v-icon>
          Demo Requests
        </button>
        <button class="action-btn outline-btn mr-2" @click="resetForm()" title="Clear Form Fields">
          <v-icon left size="16" color="#64748b">mdi-refresh</v-icon>
          Reset
        </button>
        <button
          class="action-btn primary-dark-btn"
          :disabled="appLoading"
          @click="scrollToForm()"
        >
          <v-icon left size="16" color="#ffffff">mdi-plus</v-icon>
          Add Jewellery Admin
        </button>
      </div>
    </header>

    <!-- 2. TOP SYSTEM OVERVIEW METRICS (4 CARDS) -->
    <section class="sa-kpi-grid mb-4">
      <div class="sa-kpi-card">
        <div class="kpi-top">
          <div class="kpi-icon-box">
            <v-icon size="20" color="#334155">mdi-store-outline</v-icon>
          </div>
          <span class="kpi-pill">Stores</span>
        </div>
        <div class="kpi-label">TOTAL REGISTERED ADMINS</div>
        <div class="kpi-value">{{ totalAdminsCount !== null ? totalAdminsCount : '—' }}</div>
        <div class="kpi-footer">
          <span class="kpi-meta">Active jewellery store accounts</span>
        </div>
      </div>

      <div class="sa-kpi-card">
        <div class="kpi-top">
          <div class="kpi-icon-box">
            <v-icon size="20" color="#334155">mdi-key-outline</v-icon>
          </div>
          <span class="kpi-pill">Security</span>
        </div>
        <div class="kpi-label">CREDENTIAL PROVISIONING</div>
        <div class="kpi-value text-sm-label">Auto-Generated</div>
        <div class="kpi-footer">
          <span class="kpi-meta">Mandatory password reset on first login</span>
        </div>
      </div>

      <div class="sa-kpi-card">
        <div class="kpi-top">
          <div class="kpi-icon-box">
            <v-icon size="20" color="#334155">mdi-shield-crown-outline</v-icon>
          </div>
          <span class="kpi-pill">Role</span>
        </div>
        <div class="kpi-label">ACCESS LEVEL</div>
        <div class="kpi-value text-sm-label">Super Administrator</div>
        <div class="kpi-footer">
          <span class="kpi-meta">Full cross-store management access</span>
        </div>
      </div>

      <div class="sa-kpi-card">
        <div class="kpi-top">
          <div class="kpi-icon-box">
            <v-icon size="20" color="#166534">mdi-check-circle-outline</v-icon>
          </div>
          <span class="kpi-pill pill-status">Operational</span>
        </div>
        <div class="kpi-label">SYSTEM STATUS</div>
        <div class="kpi-value text-sm-label">All Services Online</div>
        <div class="kpi-footer">
          <span class="kpi-meta">API &amp; Database synchronized</span>
        </div>
      </div>
    </section>

    <!-- 3. STRUCTURED ONBOARDING FORM -->
    <section class="sa-form-card mb-5" ref="formCard" id="adminFormSection">
      <div class="form-card-header">
        <div class="d-flex align-center">
          <v-icon size="18" color="#334155" class="mr-2">mdi-account-plus-outline</v-icon>
          <h2 class="form-card-title">Add Jewellery Admin Form</h2>
        </div>
        <span class="required-legend">* Mandatory Fields</span>
      </div>

      <v-form ref="adminForm" lazy-validation class="pa-5">
        <!-- SECTION 1: STORE & ADMINISTRATOR IDENTITY -->
        <div class="form-section-block mb-5">
          <div class="section-title-wrap">
            <span class="section-number">1</span>
            <div>
              <h3 class="section-heading">Store &amp; Administrator Identity</h3>
              <p class="section-desc">Primary contact information and administrative login credentials</p>
            </div>
          </div>

          <v-layout wrap class="mt-2">
            <v-flex xs12 sm6 md4 pa-2>
              <label class="field-label">Jewellery Store Name <span class="text-danger">*</span></label>
              <v-text-field
                v-model="name"
                placeholder="e.g. Malabar Gold & Diamonds"
                outlined
                dense
                hide-details="auto"
                :rules="[rules.required]"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 md4 pa-2>
              <label class="field-label">Admin Username <span class="text-danger">*</span></label>
              <v-text-field
                v-model="username"
                placeholder="e.g. malabar_admin"
                outlined
                dense
                hide-details="auto"
                :rules="[rules.required]"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 md4 pa-2>
              <label class="field-label">Email Address <span class="text-danger">*</span></label>
              <v-text-field
                v-model="email"
                type="email"
                placeholder="admin@jewellery.com"
                outlined
                dense
                hide-details="auto"
                :rules="[rules.required, rules.email]"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 md4 pa-2>
              <label class="field-label">Phone Number <span class="text-danger">*</span></label>
              <v-text-field
                v-model="phone"
                type="number"
                placeholder="10-digit mobile number"
                outlined
                dense
                hide-details="auto"
                :rules="phoneRules"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 md8 pa-2>
              <label class="field-label">Store Physical Address &amp; City <span class="text-danger">*</span></label>
              <v-textarea
                v-model="address"
                placeholder="Shop No., Street Name, City, State, Pincode"
                outlined
                dense
                rows="2"
                hide-details="auto"
                :rules="[rules.required]"
              ></v-textarea>
            </v-flex>
          </v-layout>
        </div>

        <v-divider class="section-divider mb-5"></v-divider>

        <!-- SECTION 2: STATUTORY & TAX COMPLIANCE -->
        <div class="form-section-block mb-5">
          <div class="section-title-wrap">
            <span class="section-number">2</span>
            <div>
              <h3 class="section-heading">Tax &amp; Statutory Compliance</h3>
              <p class="section-desc">Government registrations, tax identifiers, and legal licensing</p>
            </div>
          </div>

          <v-layout wrap class="mt-2">
            <v-flex xs12 sm6 md4 pa-2>
              <label class="field-label">GSTIN (GST Identification)</label>
              <v-text-field
                v-model="gstin"
                placeholder="e.g. 32AAAAA0000A1Z5"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 md4 pa-2>
              <label class="field-label">PAN Card Number</label>
              <v-text-field
                v-model="pan"
                placeholder="e.g. ABCDE1234F"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 md4 pa-2>
              <label class="field-label">TAN Number</label>
              <v-text-field
                v-model="tan"
                placeholder="Tax Deduction Account No."
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 md4 pa-2>
              <label class="field-label">BIS Hallmark License</label>
              <v-text-field
                v-model="bisliscence"
                placeholder="BIS Hallmark Certification No."
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 md4 pa-2>
              <label class="field-label">Insurance Policy Number</label>
              <v-text-field
                v-model="policyno"
                placeholder="Jewellery Block Insurance No."
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 md4 pa-2>
              <label class="field-label">Place of Supply (State &amp; Code)</label>
              <v-text-field
                v-model="placeofSupply"
                placeholder="e.g. 32-Kerala"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 md4 pa-2>
              <label class="field-label">Company Registration ID</label>
              <v-text-field
                v-model="companyid"
                placeholder="CIN / LLPIN / Shop License"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-flex>
          </v-layout>
        </div>

        <v-divider class="section-divider mb-5"></v-divider>

        <!-- SECTION 3: BANKING & SETTLEMENT -->
        <div class="form-section-block mb-5">
          <div class="section-title-wrap">
            <span class="section-number">3</span>
            <div>
              <h3 class="section-heading">Banking &amp; Settlement Account</h3>
              <p class="section-desc">Designated official bank account for invoices and accounting vouchers</p>
            </div>
          </div>

          <v-layout wrap class="mt-2">
            <v-flex xs12 sm6 md4 pa-2>
              <label class="field-label">Bank Name</label>
              <v-text-field
                v-model="bankname"
                placeholder="e.g. State Bank of India"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 md4 pa-2>
              <label class="field-label">Bank Account Number</label>
              <v-text-field
                v-model="accno"
                type="number"
                placeholder="Account number"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 md4 pa-2>
              <label class="field-label">IFSC Code</label>
              <v-text-field
                v-model="ifsc"
                placeholder="e.g. SBIN0001234"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 md4 pa-2>
              <label class="field-label">Branch Name</label>
              <v-text-field
                v-model="branch"
                placeholder="e.g. Main Branch, MG Road"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-flex>
          </v-layout>
        </div>


        <!-- FORM ACTIONS STRIP -->
        <div class="form-actions-bar mt-4 pt-4">
          <div class="security-notice">
            <v-icon size="16" color="#64748b" class="mr-1">mdi-information-outline</v-icon>
            <span>A secure temporary password will be auto-generated and displayed upon submission.</span>
          </div>

          <div class="buttons-group">
            <button
              type="button"
              class="action-btn outline-btn mr-2"
              @click="resetForm()"
            >
              Clear Form
            </button>
            <button
              type="button"
              class="action-btn primary-dark-btn"
              :disabled="appLoading"
              @click="validateInput()"
            >
              <v-icon left size="16" color="#ffffff">mdi-plus</v-icon>
              Add Jewellery Admin
            </button>
          </div>
        </div>
      </v-form>
    </section>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "SuperAdminDashboard",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,

      // Form Models
      username: null,
      name: null,
      address: null,
      phone: null,
      email: null,
      gstin: null,
      tan: null,
      bisliscence: null,
      policyno: null,
      companyid: null,
      bankname: null,
      accno: null,
      ifsc: null,
      branch: null,
      pan: null,
      placeofSupply: null,

      // Image Upload
      file: null,
      imagePreview: "",

      // Live Stats
      totalAdminsCount: null,

      // Validation Rules
      rules: {
        required: (value) => !!value || "Required field.",
        email: (value) => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Invalid email address.";
        },
      },
      phoneRules: [
        (v) => !!v || "Phone is required",
        (v) => (v && String(v).length >= 10) || "Phone must be at least 10 digits",
      ],
    };
  },
  mounted() {
    this.fetchRecentAdmins();
    window.addEventListener("scroll-to-admin-form", this.scrollToForm);
    if (this.$route.hash === "#adminForm" || this.$route.hash === "#adminFormSection" || this.$route.query.scroll === "form") {
      this.scrollToForm();
    }
  },
  beforeDestroy() {
    window.removeEventListener("scroll-to-admin-form", this.scrollToForm);
  },
  methods: {
    scrollToForm() {
      this.$nextTick(() => {
        const el =
          this.$refs.formCard ||
          (this.$refs.adminForm
            ? this.$refs.adminForm.$el || this.$refs.adminForm
            : document.querySelector(".sa-form-card"));
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          const firstInput = el.querySelector("input");
          if (firstInput) {
            setTimeout(() => firstInput.focus(), 400);
          }
        }
      });
    },
    fetchRecentAdmins() {
      axios({
        url: "/user/all/jewelleryadmins",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          count: 5,
          page: 1,
        },
      })
        .then((res) => {
          if (res.data.status) {
            this.totalAdminsCount = res.data.count || 0;
          }
        })
        .catch((err) => {
          console.error("Could not fetch admin count:", err);
        });
    },
    validateInput() {
      if (!this.email) {
        this.msg = "Please provide an email address";
        this.showsnackbar = true;
        return;
      }
      if (!this.name) {
        this.msg = "Please provide the jewellery store name";
        this.showsnackbar = true;
        return;
      }
      if (!this.phone) {
        this.msg = "Please provide contact phone number";
        this.showsnackbar = true;
        return;
      }
      if (!this.username) {
        this.msg = "Please provide a username";
        this.showsnackbar = true;
        return;
      }
      if (!this.address) {
        this.msg = "Please provide store physical address";
        this.showsnackbar = true;
        return;
      }

      this.addAdmin();
    },
    showImage(e) {
      const files = e.target.files;
      if (!files || !files.length) return;
      this.file = files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        this.imagePreview = event.target.result;
      };
      if (/\.(jpe?g|png|gif|webp)$/i.test(this.file.name)) {
        reader.readAsDataURL(this.file);
      }
    },
    clearImage() {
      this.file = null;
      this.imagePreview = "";
      if (this.$refs.showImage) {
        this.$refs.showImage.value = "";
      }
    },
    resetForm() {
      this.username = null;
      this.name = null;
      this.address = null;
      this.phone = null;
      this.email = null;
      this.gstin = null;
      this.tan = null;
      this.bisliscence = null;
      this.policyno = null;
      this.companyid = null;
      this.bankname = null;
      this.accno = null;
      this.ifsc = null;
      this.branch = null;
      this.pan = null;
      this.placeofSupply = null;
      this.clearImage();
      if (this.$refs.adminForm) {
        this.$refs.adminForm.resetValidation();
      }
    },
    uploadImage(userId) {
      if (!this.file) return;
      let formData = new FormData();
      formData.append("photos", this.file);
      if (userId) {
        formData.append("userId", userId);
      }

      axios({
        method: "POST",
        url: "/user/uploadimagesingle",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
          "x-auth-token": localStorage.getItem("token"),
        },
      }).catch((err) => {
        console.error("Image upload failed:", err);
      });
    },
    addAdmin() {
      this.appLoading = true;
      axios({
        url: "/user/jewelleryregister",
        method: "POST",
        data: {
          username: this.username,
          email: this.email,
          jewelleryName: this.name,
          phone: this.phone,
          location: this.address,
          GSTIN: this.gstin,
          TAN: this.tan,
          BisLicense: this.bisliscence,
          policyNo: this.policyno,
          companyId: this.companyid,
          bankName: this.bankname,
          accountNo: this.accno,
          IFSCCode: this.ifsc,
          branch: this.branch,
          pancardNo: this.pan,
          stateandCode: this.placeofSupply,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            if (response.data.id && this.file) {
              this.uploadImage(response.data.id);
            }
            const pwdMsg = response.data.generatedPassword
              ? `Jewellery Admin Successfully Provisioned!\n\nStore: ${this.name}\nUsername: ${this.username}\nGenerated Password: ${response.data.generatedPassword}\n\nPlease share these credentials securely with the store administrator.`
              : (response.data.msg || "Admin registered successfully");

            alert(pwdMsg);
            this.msg = response.data.msg || "Admin registered successfully";
            this.showsnackbar = true;
            this.resetForm();
            this.fetchRecentAdmins();
          } else {
            this.msg = response.data.msg || "Failed to register admin";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
  },
};
</script>

<style scoped>
/* Main Container */
.sa-dashboard-container {
  padding: 8px 4px 40px 4px;
  max-width: 1600px;
  margin: 0 auto;
  font-family: 'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #0f172a;
}

/* 1. Header */
.sa-hero-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.sa-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.sa-main-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin: 0;
  line-height: 1.2;
}

.role-badge-pill {
  display: inline-flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  color: #334155;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 6px;
  margin-left: 8px;
}

.sa-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0 0;
}

.sa-hero-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.action-btn {
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
}

.outline-btn {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.outline-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.text-btn {
  background: transparent;
  border: none;
  color: #dc2626;
  padding: 0 8px;
}

.text-btn:hover {
  text-decoration: underline;
}

.primary-dark-btn {
  background: #1e293b;
  color: #ffffff;
  border: 1px solid #1e293b;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.primary-dark-btn:hover:not(:disabled) {
  background: #0f172a;
  border-color: #0f172a;
}

.primary-dark-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 2. KPI Grid */
.sa-kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

@media (max-width: 1100px) {
  .sa-kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .sa-kpi-grid {
    grid-template-columns: 1fr;
  }
}

.sa-kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
}

.kpi-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.kpi-icon-box {
  width: 34px;
  height: 34px;
  border-radius: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-pill {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #475569;
}

.pill-status {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.3px;
  line-height: 1.2;
  margin-bottom: 8px;
  color: #0f172a;
}

.text-sm-label {
  font-size: 16px !important;
  font-weight: 700;
  padding-top: 4px;
}

.kpi-footer {
  border-top: 1px solid #f1f5f9;
  padding-top: 8px;
}

.kpi-meta {
  font-size: 12px;
  color: #64748b;
}

/* 3. Form Card */
.sa-form-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  overflow: hidden;
}

.form-card-header {
  padding: 14px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.form-card-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.required-legend {
  font-size: 11.5px;
  font-weight: 600;
  color: #dc2626;
}

.form-section-block {
  padding: 4px 0;
}

.section-title-wrap {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
}

.section-number {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: #0f172a;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.section-heading {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 2px 0;
}

.section-desc {
  font-size: 12px;
  color: #64748b;
  margin: 0;
}

.section-divider {
  border-color: #f1f5f9 !important;
}

.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 5px;
}

.text-danger {
  color: #dc2626;
  font-weight: 700;
}

/* Avatar Preview */
.avatar-preview-box {
  width: 120px;
  height: 120px;
  border-radius: 8px;
  border: 1.5px dashed #cbd5e1;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px;
  text-align: center;
}

.placeholder-text {
  font-size: 10.5px;
  color: #94a3b8;
  margin-top: 4px;
}

.upload-controls-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.file-name-text {
  font-size: 12px;
  color: #475569;
  font-weight: 500;
}

.upload-help-text {
  font-size: 11.5px;
  color: #64748b;
  margin: 0;
}

/* Form Actions */
.form-actions-bar {
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.security-notice {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #64748b;
}

.buttons-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 4. Recent Table */
.sa-table-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  overflow: hidden;
}

.table-card-header {
  padding: 12px 18px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.table-card-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.view-all-link-btn {
  background: transparent;
  border: none;
  color: #0f172a;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.view-all-link-btn:hover {
  text-decoration: underline;
}

.table-responsive {
  overflow-x: auto;
}

.corporate-data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.corporate-data-table th {
  background: #ffffff;
  color: #64748b;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 10px 18px;
  border-bottom: 1px solid #e2e8f0;
}

.corporate-data-table td {
  padding: 11px 18px;
  border-bottom: 1px solid #f1f5f9;
  color: #1e293b;
  vertical-align: middle;
}

.table-row:hover {
  background-color: #f8fafc;
}

.text-dark {
  color: #0f172a;
}

.text-muted {
  color: #64748b;
}

.status-chip {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
}

.active-chip {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
}
</style>
